import React from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import {
  useCreatePromptTemplateMutation,
  useUpdatePromptTemplateMutation,
  useGetPromptTemplateByIdQuery,
} from "../../../../Api/attoDeskApi";
import { useNotifier } from "../../../../Core/Notifier";

import { LegacyTokens } from "../../../../theme/b13Tokens";
import PromptLevelEditorBody, {
  EditorLevel, makeEmptyLevel, normaliseLevel,
} from "../shared/PromptLevelEditorBody";
import PromptLevelModal from "../shared/PromptLevelModal";
import { OutcomeMode, DEFAULT_OUTCOME_MODE, normaliseOutcomeMode } from "../shared/outcomeModeConstants";

// Batch 6 (rewrite) — Prompt Level Template editor.
//
// This route now renders the same PromptLevelModal used elsewhere so the
// editing surface is visually identical whether the user comes from
// Settings or from the Program editor's QuickHierarchyDrawer.
//
// Removed:
//   - "Save Draft" button (§B: one SAVE, no draft state in UI)
//   - "Cannot save & activate: …" floating error above the footer (§C:
//     inline field errors and a truly-disabled SAVE)
//   - The right-hand "Prompting Ladder / Support Ladder" preview panel
//     (D2: cut — the level cards themselves are the ordered ladder)
//   - Full-page Grid + two-column layout (replaced by the modal card)
//
// Kept (moved into "Advanced settings" collapse on each level card,
// per §6.3(f) — nothing deleted, only relocated):
//   - Short Code, Independence Score, per-level Description, Active,
//     Duplicate level
// Kept (moved into the gear popover, per §6.2):
//   - Description, Use as default, Ordering direction, Reverse order

// Standard seven-level hierarchy for the ?preset=standard path.
const STANDARD_LEVELS = [
  { levelName: "Full Physical",    shortCode: "FP", score: 10,  colour: LegacyTokens.red,     outcomeState: "not_counted"    as const, description: "Hand-over-hand support through the full response." },
  { levelName: "Partial Physical", shortCode: "PP", score: 25,  colour: LegacyTokens.clay,    outcomeState: "not_counted"    as const, description: "Physical support at the elbow / shoulder only." },
  { levelName: "Model",            shortCode: "M",  score: 40,  colour: LegacyTokens.amber,   outcomeState: "not_counted"    as const, description: "Demonstrate the response for the learner to imitate." },
  { levelName: "Gestural",         shortCode: "G",  score: 55,  colour: LegacyTokens.teal,    outcomeState: "not_counted"    as const, description: "Point / gesture toward the correct response." },
  { levelName: "Verbal",           shortCode: "V",  score: 70,  colour: LegacyTokens.blue,    outcomeState: "not_counted"    as const, description: "Verbal cue that names or hints at the response." },
  { levelName: "Visual",           shortCode: "Vi", score: 85,  colour: LegacyTokens.purple,  outcomeState: "not_counted"    as const, description: "Visual cue (image, text, arrow, model)." },
  { levelName: "Independent",      shortCode: "I",  score: 100, colour: LegacyTokens.emerald, outcomeState: "successful" as const, description: "Learner responds without any prompt." },
];

export default function PromptHierarchyEditor({ mode }: { mode: "create" | "view" | "edit" }) {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [search] = useSearchParams();
  const preset = search.get("preset");
  const { showMessage, showErrorMessage } = useNotifier();
  const readOnly = mode === "view";

  const { data: fetched, isFetching: fetching } = useGetPromptTemplateByIdQuery(id!, { skip: !id });
  const [create, { isLoading: creating }] = useCreatePromptTemplateMutation();
  const [update, { isLoading: updating }] = useUpdatePromptTemplateMutation();

  // ─── form state ───────────────────────────────────────────────────────
  const [templateName, setTemplateName] = React.useState("");
  const [description, setDescription]   = React.useState("");
  const [direction, setDirection]       = React.useState<string>("most_to_least");
  const [isDefault, setIsDefault]       = React.useState(false);
  const [outcomeMode, setOutcomeMode]   = React.useState<OutcomeMode>(DEFAULT_OUTCOME_MODE);
  const [levels, setLevels]             = React.useState<EditorLevel[]>([makeEmptyLevel(0)]);
  const [dirty, setDirty]               = React.useState(false);
  const [validated, setValidated]       = React.useState(false);

  // Refs so a failed SAVE can focus and scroll to the first invalid field.
  const templateNameRef = React.useRef<HTMLInputElement | null>(null);
  const levelNameRefs = React.useRef<Record<number, HTMLInputElement | null>>({});

  // ─── preload ─────────────────────────────────────────────────────────
  React.useEffect(() => {
    if (fetched?.data) {
      const t: any = fetched.data;
      setTemplateName(t.templateName || "");
      setDescription(t.description || "");
      setDirection(t.direction || "most_to_least");
      setIsDefault(!!t.isDefault);
      setOutcomeMode(normaliseOutcomeMode(t.outcomeMode));
      const ls = (t.levels || []).map((l: any, i: number) => normaliseLevel(l, i));
      setLevels(ls.length ? ls : [makeEmptyLevel(0)]);
      setDirty(false);
    } else if (preset === "standard" && mode === "create") {
      setTemplateName("Standard Prompt Levels");
      setDescription("Fade prompts from most to least intrusive as the learner becomes more independent.");
      setLevels(
        STANDARD_LEVELS.map((l, i) =>
          normaliseLevel({
            ...l,
            isActive: true,
            countsAsIndependent: l.outcomeState === "successful",
            orderNumber: i,
          }, i)
        )
      );
    }
  }, [fetched, preset, mode]);

  // Track dirty state so the modal close-guard can prompt.
  const markDirty = React.useCallback(<T,>(setter: React.Dispatch<React.SetStateAction<T>>) =>
    (v: T | ((p: T) => T)) => { setDirty(true); setter(v as any); }, []);

  // ─── validation ──────────────────────────────────────────────────────
  const templateNameError = React.useMemo<string | null>(() => {
    if (!validated) return null;
    // Batch 6B §6B.4 — copy is TEMPLATE-specific, not "for this template."
    if (!templateName.trim()) return "Please enter a name for the template";
    return null;
  }, [templateName, validated]);

  const levelErrors: Record<number, string | null> = React.useMemo(() => {
    if (!validated) return {};
    const out: Record<number, string | null> = {};
    levels.forEach((lv, i) => {
      if (!lv.levelName.trim()) out[i] = "Level name is required.";
    });
    // Duplicate detection
    const seen = new Map<string, number>();
    levels.forEach((lv, i) => {
      const key = lv.levelName.trim().toLowerCase();
      if (!key) return;
      if (seen.has(key)) out[i] = "Duplicate level name.";
      else seen.set(key, i);
    });
    return out;
  }, [levels, validated]);

  const hasErrors = Boolean(templateNameError) || Object.values(levelErrors).some(Boolean);
  const disableSave = validated && hasErrors;

  // ─── save ────────────────────────────────────────────────────────────
  const focusFirstInvalid = () => {
    if (templateNameError) {
      templateNameRef.current?.focus();
      templateNameRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    const firstBadIdx = Object.keys(levelErrors)
      .map(Number)
      .filter((i) => levelErrors[i])
      .sort((a, b) => a - b)[0];
    if (firstBadIdx !== undefined) {
      const el = levelNameRefs.current[firstBadIdx];
      el?.focus();
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const save = async () => {
    setValidated(true);
    // Recompute synchronously — validated state above will be true in the
    // next render, so recompute inline instead of trusting the memo yet.
    if (!templateName.trim()) { setTimeout(focusFirstInvalid, 0); return; }
    const badLevel = levels.findIndex((l) => !l.levelName.trim());
    if (badLevel >= 0) { setTimeout(focusFirstInvalid, 0); return; }

    const payload = {
      id: id ? Number(id) : undefined,
      templateName: templateName.trim(),
      description: description || null,
      objective: "Increase Percent Correct",
      direction,
      status: "active",
      isDefault,
      // Batch 6A — persist the outcome mode. Backend column added in the
      // migration that ships with this batch.
      outcomeMode,
      levels: levels
        .filter((l) => l.levelName.trim())
        .map((l, i) => ({
          levelName: l.levelName.trim(),
          shortCode: l.shortCode || null,
          description: l.description || null,
          score: l.score,
          // Batch 6A §1 — weightedScore travels as its own field; null
          // stays null (never coerced to a number).
          weightedScore: l.weightedScore === null ? null : l.weightedScore,
          colour: l.colour,
          countsAsIndependent: l.outcomeState === "successful",
          outcomeState: l.outcomeState,
          isActive: l.isActive,
          orderNumber: i,
        })),
    };
    try {
      const res: any = id
        ? await update(payload).unwrap()
        : await create(payload).unwrap();
      if (res?.status) {
        showMessage(res.message || "Saved");
        setDirty(false);
        navigate("/aba-therapy/settings/prompt-levels");
      } else {
        showErrorMessage(res?.message || "Save failed");
      }
    } catch {
      showErrorMessage("Save failed");
    }
  };

  const busy = creating || updating;

  return (
    <Box sx={{ position: "relative", minHeight: "70vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      {/* Batch 6 §2.4 — visible loading indicator on the wrapper Box.
         Prevents a bare dark scrim while the fetched template loads on
         deep-link navigations. Sits behind the modal scrim, so it is
         only visible in the ~200ms before the modal paints. */}
      {fetching && (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
          <CircularProgress size={28} />
          <Typography sx={{ fontSize: 12, color: LegacyTokens.ink3 }}>Loading template…</Typography>
        </Box>
      )}
      <PromptLevelModal
        open
        title={
          mode === "create" ? "Add prompt level template"
            : mode === "view" ? (templateName || "Prompt level template")
              : "Edit prompt level template"
        }
        onClose={() => navigate("/aba-therapy/settings/prompt-levels")}
        onSave={save}
        saving={busy}
        disableSave={disableSave}
        hasUnsavedChanges={dirty}
      >
        <PromptLevelEditorBody
          templateName={templateName}
          onTemplateNameChange={(v) => { setDirty(true); setTemplateName(v); }}
          templateNameError={templateNameError}
          description={description}
          onDescriptionChange={(v) => { setDirty(true); setDescription(v); }}
          isDefault={isDefault}
          onDefaultChange={(v) => { setDirty(true); setIsDefault(v); }}
          direction={direction}
          onDirectionChange={(v) => { setDirty(true); setDirection(v); }}
          onReverse={() => {
            setDirty(true);
            setLevels((L) => [...L].reverse().map((l, i) => ({ ...l, orderNumber: i })));
          }}
          outcomeMode={outcomeMode}
          onOutcomeModeChange={(m) => { setDirty(true); setOutcomeMode(m); }}
          levels={levels}
          onLevelsChange={markDirty(setLevels)}
          levelErrors={levelErrors}
          registerNameRef={(el) => { templateNameRef.current = el; }}
          registerLevelNameRef={(i, el) => { levelNameRefs.current[i] = el; }}
          readOnly={readOnly}
        />
      </PromptLevelModal>
    </Box>
  );
}
